var $ = require('jquery');
var _ = require('underscore');
var TorsoEvents = require('backbone-torso/modules/Events');
var TorsoService = require('backbone-torso/modules/ServiceCell');
var storedPaymentMethodCollection = require('./storedPaymentMethodCollection');
var financialFeatureConfigModel = require('./../financial/financialFeatureConfigModel');
var StoredPaymentMethodModel = require('./StoredPaymentMethodModel');

/**
* Service for handling saving payment methods for payment information views
* @class StoredPaymentMethodService
* @constructor
*/
module.exports = TorsoService.extend({

  /**
   * @method intialize
   * @override
   * @param options
   *   @param options.formModel {Torso.FormModel} form model containing bindings
   *   @param options.patientVecnaId {String} the vecna id of the patient that the stored payments are for.
   */
  initialize: function(options) {
    if (!options) {
      throw 'Missing required arguments when initializing StoredPaymentMethodService';
    }

    _.bindAll(this, '_saveStoredPaymentMethod', '_deleteStoredPaymentMethod', 'createStoredPaymentMethod',
                    '_untrackAndKillStoredPaymentModels', '_pullSavedStoredPaymentMethodToFormModel', '_setSaveStoredPaymentMethodFailed');
    this._patientVecnaId = options.patientVecnaId;
    this._formModel = options.formModel;

    // Register this service on the form model so it can call for nicknames.
    if (this._formModel && _.isFunction(this._formModel.setStoredPaymentMethodService)) {
      this._formModel.setStoredPaymentMethodService(this);
    }

    financialFeatureConfigModel.loadOnce();
    this._patientStoredPaymentMethodsCollection = storedPaymentMethodCollection.createPrivateCollection(this.cid);
    this._fetchPatientStoredPayments();
  },

  /**
   * Dispose of this service and the private collection it creates.
   * @method _dispose
   * @private
   */
  _dispose: function() {
    this._patientStoredPaymentMethodsCollection.dispose();
  },

  /**
   * Get all the stored payment methods of a user and load them so we can validate nicknames and things.
   * @method _fetchPatientStoredPayments
   * @return {Deferred} a promise that will resolve when the stored payments have been loaded.
   * @private
   */
  _fetchPatientStoredPayments: function() {
    // If the patient vecna id isn't set then resolve immediately with no results.
    if (!this._patientVecnaId) {
      return $.Deferred().resolve().promise();
    }
    var privateStoredPaymentMethodsCollection = this._patientStoredPaymentMethodsCollection;
    this._patientStoredPaymentsPromise = privateStoredPaymentMethodsCollection.fetchIdsForPatient(this._patientVecnaId)
      .then(function(ids) {
        return privateStoredPaymentMethodsCollection.trackAndPull(ids);
      });
    return this._patientStoredPaymentsPromise;
  },

  /**
   * Get all nicknames of stored payment methods from the patient's list of stored payment methods.
   * @method getAllStoredPaymentMethodNicknamesForPatient
   */
  getAllStoredPaymentMethodNicknamesForPatient: function() {
    return this._patientStoredPaymentMethodsCollection.pluck('nickname');
  },

  /**
   * Chains together all the saves/updates need to create a complete storedPaymentMethod
   * @method createStoredPaymentMethod
   * @return {Deferred} promise from model save chain
   */
  createStoredPaymentMethod: function() {
    this._clearCreateFailures();

    var savePaymentsPromise;
    if (this._shouldSavePayments()) {
      savePaymentsPromise = this._saveStoredPaymentMethod();
    } else {
      savePaymentsPromise = $.Deferred().resolve().promise();
    }
    var storedPaymentMethodService = this;
    return savePaymentsPromise
      .then(function() {
        TorsoEvents.trigger('payment-methods:updated');
        storedPaymentMethodService.trigger('payment-methods:updated');
      })
      .then(this._untrackAndKillStoredPaymentModels, this._untrackAndKillStoredPaymentModels);
  },

  /**
   * Create the stored payment method without token
   *
   * @method _saveStoredPaymentMethod
   * @private
   * @return {Promise} a promise for saving this._storedPaymentMethodModel
   */
  _saveStoredPaymentMethod: function() {
    this._storedPaymentMethodModel = new StoredPaymentMethodModel({ patientVecnaId: this._patientVecnaId });
    this._formModel.trackModel('storedPaymentMethod', this._storedPaymentMethodModel);
    this._formModel.push();
    return this._storedPaymentMethodModel.save()
      .then(this._pullSavedStoredPaymentMethodToFormModel, this._setSaveStoredPaymentMethodFailed);
  },

  /**
   * Pull the stored payment data from the just saved stored payment method to the form model.
   * @method _pullSavedStoredPaymentMethodToFormModel
   * @private
   */
  _pullSavedStoredPaymentMethodToFormModel: function() {
    this._formModel.pull();
  },

  /**
   * Set that saving the stored payment method failed.
   *
   * @method _setSaveStoredPaymentMethodFailed
   * @private
   */
  _setSaveStoredPaymentMethodFailed: function() {
    this.set('errors', {
      saveStoredPaymentMethod: true
    });
  },

  /**
   * Clear any failures logged by previous attempts to create.
   *
   * @method _clearCreateFailures
   * @private
   */
  _clearCreateFailures: function() {
    this.set('errors', {
      saveStoredPaymentMethod: false,
      createSecurePaymentToken: false
    });
  },

  /**
   * Untrack the stored payment models and remove references to them.
   * @method _untrackAndKillStoredPaymentModels
   * @private
   */
  _untrackAndKillStoredPaymentModels: function() {
    this._formModel.untrackModel('storedPaymentMethod');
    delete this._storedPaymentMethodModel;
    this._formModel.untrackModel('paymentMethodToken');
    delete this._paymentMethodTokenModel;
  },

  /**
   * Delete the stored payment method, delete the token
   *
   * @method deletePaymentMethod
   * @param storedPaymentMethod {StoredPaymentMethodModel} the model of the payment method to delete.
   */
  deletePaymentMethod: function(storedPaymentMethod) {
    this._clearDeleteFailures();
    return this._deleteStoredPaymentMethod(storedPaymentMethod);
  },

  /**
   * Clear any failures logged by previous attempts to delete.
   *
   * @method _clearDeleteFailures
   * @private
   */
  _clearDeleteFailures: function() {
    this.set('errors', {
      deleteStoredPaymentMethodFailed: false
    });
  },

  /**
   * Delete a saved payment method from external service and delete stored payment method
   *
   * @method _deleteStoredPaymentMethod
   * @param storedPaymentMethod {StoredPaymentMethodModel} the model of the payment method to delete.
   * @private
   */
  _deleteStoredPaymentMethod: function(storedPaymentMethod) {
    return storedPaymentMethod.destroy()
      .fail(function() {
        this.set('errors', {
          deleteStoredPaymentMethodFailed: true
        });
      }.bind(this));
  },

  /**
   *
   * @return whether or not payment methods should be saved
   */
  _shouldSavePayments: function() {
    var shouldSavePayment = this._formModel.get('storePayment.save') || this._formModel.get('isStoredPayment');
    var isEnabled = financialFeatureConfigModel.isStoredPaymentMethodsEnabled();

    return shouldSavePayment && isEnabled;
  }
});
